"use client"

import { useEffect, useState } from "react"
import { Label } from "@/components/Label/Label.component"
import { useChronology } from "@/contexts/chronology/chronology.context"
import { useSite } from "@/contexts/site/site.context"
import type { ZonedDateTime } from "@/ts/date-time/date-time.types"
import { PublicationDateTimeLoader } from "../PublicationDateTimeLoader/PublicationDateTimeLoader.component"

type LongPublicationDateTimeProps = {
  readonly zonedDateTime: ZonedDateTime
  readonly className?: string
}

export function LongPublicationDateTime({
  zonedDateTime,
  className,
}: LongPublicationDateTimeProps) {
  const { locale } = useSite()
  const { toLongDateString, toShortTimeString } = useChronology()
  const [formattedDate, setFormattedDate] = useState<null | string>(null)
  const [formattedTime, setFormattedTime] = useState<null | string>(null)

  useEffect(() => {
    // Please note that because we are using `new Date()` here, we have
    // to wrap it in a `useEffect` hook to avoid hydration issues.
    // We can safely use `new Date()` in the browser because time
    // zone information is specified in the `zonedDateTime` prop.
    const dateToFormat = new Date(zonedDateTime)

    setFormattedDate(toLongDateString(dateToFormat))
    setFormattedTime(toShortTimeString(dateToFormat))
  }, [toLongDateString, toShortTimeString, zonedDateTime])

  return (
    <Label className={className}>
      {formattedDate !== null || formattedTime !== null ? (
        <time className="space-x-[0.5ch]" dateTime={zonedDateTime}>
          {formattedDate !== null ? <span>{formattedDate}</span> : null}

          {formattedTime !== null ? (
            <span>
              {
                {
                  da: `kl. ${formattedTime}`,
                  de: `${formattedTime} Uhr`,
                  en: `at ${formattedTime}`,
                  no: `kl. ${formattedTime}`,
                  sv: `kl. ${formattedTime}`,
                }[locale]
              }
            </span>
          ) : null}
        </time>
      ) : (
        <PublicationDateTimeLoader className="w-20" />
      )}
    </Label>
  )
}
